import {
  Badge,
  Button,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import type { OvertimeRequestListItem } from "@/modules/hr/overtime/domain/types";
import { formatDateWithWeekdayLao } from "@/shared/lib/date-time";

const statusLabel: Record<OvertimeRequestListItem["status"], string> = {
  pending: "ລໍຖ້າອະນຸມັດ",
  approved: "ອະນຸມັດແລ້ວ",
  rejected: "ປະຕິເສດ",
};

type OTRequestListTableProps = {
  data: OvertimeRequestListItem[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onViewDetails: (row: OvertimeRequestListItem) => void;
};

export function OTRequestListTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onViewDetails,
}: OTRequestListTableProps) {
  const columns: TanstackReactTable.ColumnDef<OvertimeRequestListItem>[] = [
    createSortableColumn<OvertimeRequestListItem>("workDate", "ວັນທີ", {
      size: 180,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDateWithWeekdayLao(row.original.workDate)}
        </span>
      ),
    }),
    createSortableColumn<OvertimeRequestListItem>("startTime", "ເວລາເລີ່ມ", {
      size: 100,
      cell: ({ row }) => (
        <span className="tabular-nums">{row.original.startTime}</span>
      ),
    }),
    createSortableColumn<OvertimeRequestListItem>("endTime", "ເວລາສິ້ນສຸດ", {
      size: 100,
      cell: ({ row }) => (
        <span className="tabular-nums">{row.original.endTime}</span>
      ),
    }),
    createSortableColumn<OvertimeRequestListItem>("hours", "ຊົ່ວໂມງລວມ", {
      size: 110,
      cell: ({ row }) => (
        <span className="font-medium tabular-nums">
          {row.original.hours.toFixed(1)} ຊມ.
        </span>
      ),
    }),
    createSortableColumn<OvertimeRequestListItem>("status", "ສະຖານະ", {
      size: 110,
      cell: ({ row }) => {
        const s = row.original.status;
        const variant =
          s === "approved"
            ? "default"
            : s === "rejected"
              ? "destructive"
              : "secondary";
        return <Badge variant={variant}>{statusLabel[s]}</Badge>;
      },
    }),
    {
      id: "actions",
      header: "ການດຳເນີນການ",
      cell: ({ row }) => (
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0 font-normal"
          onClick={() => onViewDetails(row.original)}
        >
          ເບິ່ງລາຍລະອຽດ
        </Button>
      ),
      size: 100,
    },
  ];

  return (
    <DataTable<OvertimeRequestListItem, unknown>
      noDataMessage="ບໍ່ພົບຄຳຂໍ OT."
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id ?? "workDate", !!sorting[0]?.desc);
      }}
    />
  );
}
